// Campaigns, audiences, creatives and pixels — the operator-owned entities.
//
// Direct D1 entity reads are authoritative and revision-controlled. Every
// `byId` gates on a truthy id at the call site: the resource describes the read,
// and whether a given screen has an id yet is that screen's business.

import { api } from '@/api/client';
import { defineQuery } from '@/api/resource';
import { buildQs } from '@/api/query-string';
import type {
  AudienceResponse,
  AudiencesListResponse,
  CampaignResponse,
  CampaignsListResponse,
  CreativeResponse,
  CreativesListResponse,
  PixelResponse,
  PixelsListResponse,
} from '@/api/types';
import type { PixelDefaults } from '@/api/types.gen';

export interface CampaignsQuery {
  status?: string;
  channel?: string;
  production_id?: string;
  audience_type?: string;
  q?: string;
}

export interface AudiencesQuery {
  audience_type?: string;
  production_id?: string;
  q?: string;
}

export interface CreativesQuery {
  status?: string;
  channel?: string;
  production_id?: string;
  q?: string;
}

export interface PixelsQuery {
  status?: string;
  q?: string;
}

export const campaignsList = defineQuery({
  namespace: 'authoritative',
  name: 'campaigns.list',
  domains: ['campaigns'],
  request: (q: CampaignsQuery, signal) =>
    api.get<CampaignsListResponse>(`/campaigns${buildQs(q)}`, { signal }),
  unwrap: (r) => r.campaigns,
});

export const campaignById = defineQuery({
  namespace: 'authoritative',
  name: 'campaigns.byId',
  domains: ['campaigns'],
  keyOf: (id: string) => [id],
  request: (id: string, signal) =>
    api.get<CampaignResponse>(`/campaigns/${encodeURIComponent(id)}`, { signal }),
  unwrap: (r) => r.campaign,
});

export const audiencesList = defineQuery({
  namespace: 'authoritative',
  name: 'audiences.list',
  domains: ['audiences'],
  request: (q: AudiencesQuery, signal) =>
    api.get<AudiencesListResponse>(`/audiences${buildQs(q)}`, { signal }),
  unwrap: (r) => r.audiences,
});

export const audienceById = defineQuery({
  namespace: 'authoritative',
  name: 'audiences.byId',
  domains: ['audiences'],
  keyOf: (id: string) => [id],
  request: (id: string, signal) =>
    api.get<AudienceResponse>(`/audiences/${encodeURIComponent(id)}`, { signal }),
  unwrap: (r) => r.audience,
});

// A creative carries the campaigns that pin it (`pinned_by`) with their
// status and phase, so a campaign write (a publish, a pause, an archive, a
// runtime observation) makes it stale too (2026-10-19).
export const creativesList = defineQuery({
  namespace: 'authoritative',
  name: 'creatives.list',
  domains: ['creatives', 'campaigns'],
  request: (q: CreativesQuery, signal) =>
    api.get<CreativesListResponse>(`/creatives${buildQs(q)}`, { signal }),
  unwrap: (r) => r.creatives,
});

export const creativeById = defineQuery({
  namespace: 'authoritative',
  name: 'creatives.byId',
  domains: ['creatives', 'campaigns'],
  keyOf: (id: string) => [id],
  request: (id: string, signal) =>
    api.get<CreativeResponse>(`/creatives/${encodeURIComponent(id)}`, { signal }),
  unwrap: (r) => r.creative,
});

export const pixelsList = defineQuery({
  namespace: 'authoritative',
  name: 'pixels.list',
  domains: ['pixels'],
  request: (q: PixelsQuery, signal) =>
    api.get<PixelsListResponse>(`/pixels${buildQs(q)}`, { signal }),
  unwrap: (r) => r.pixels,
});

export const pixelById = defineQuery({
  namespace: 'authoritative',
  name: 'pixels.byId',
  domains: ['pixels'],
  keyOf: (id: string) => [id],
  request: (id: string, signal) =>
    api.get<PixelResponse>(`/pixels/${encodeURIComponent(id)}`, { signal }),
  unwrap: (r) => r.pixel,
});

/**
 * Organisation-wide identifier defaults that prefill a new pixel.
 *
 * The hook restated `staleTime: 15 * 60_000` explicitly, which was the same
 * value the QueryClient default already supplied. It is now coherent with the
 * pixels/configuration revisions instead of an independent time window.
 */
export const pixelDefaults = defineQuery({
  namespace: 'authoritative',
  name: 'pixels.defaults',
  domains: ['pixels', 'configuration'],
  keyOf: () => [],
  request: (_: void, signal) => api.get<PixelDefaults>('/pixels/defaults', { signal }),
});
